import { SystemEntity } from "@erp/shared-types";
import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType, SystemEntitySchema } from "../../utils/system-entities";

const discrepancySchema = z.object({
  kind: z.enum([
    "quantity",
    "price",
    "charge",
    "unmatched_line",
    "unresolved_reference",
    "ambiguous_reference",
  ]),
  documentLineId: z.string().optional(),
  referenceLineId: z.string().optional(),
  message: z.string(),
  difference: z.number().optional(),
});

export const compareShippingDocumentStep = defineStepConfig({
  type: "compare_shipping_document",
  name: "Compare Shipping Document",
  description:
    "Extract reference tokens or compare shipping-document quantities, prices, and charges with resolved Famous orders",
  category: "Transformation",
  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT),
    classification: z.string().optional(),
    extractedDocument: z.record(z.string(), z.unknown()),
    purchaseOrders: z
      .array(entityOfType(SystemEntity.INTERNAL_PURCHASE_ORDER))
      .optional(),
    salesOrders: z.array(entityOfType(SystemEntity.AR_SALES_ORDER)).optional(),
    transferOrders: z
      .array(entityOfType(SystemEntity.IM_TRANSFER_ORDER))
      .optional(),
  }),
  outputSchema: z.object({
    referenceTokens: z.array(z.string()),
    hasResolvedDocument: z.boolean(),
    hasDiscrepancy: z.boolean(),
    ambiguous: z.boolean(),
    // The fields below are only emitted in "compare" mode; "inspect" mode
    // stops after reference-token extraction.
    selectedEntity: SystemEntitySchema.optional(),
    selectedDocumentType: z
      .enum(["purchase_order", "sales_order", "transfer_order"])
      .optional(),
    discrepancies: z.array(discrepancySchema).optional(),
    varianceSummary: z
      .object({
        quantityDifference: z.number(),
        priceDifference: z.number(),
        chargeDifference: z.number(),
      })
      .optional(),
    taskTitle: z.string().optional(),
    taskDescription: z.string().optional(),
    taskMetadata: z.record(z.string(), z.unknown()).optional(),
  }),
  configSchema: z.object({
    mode: z.enum(["inspect", "compare"]).default("compare"),
    quantityTolerancePercent: z.number().min(0).default(0.05),
    priceTolerancePercent: z.number().min(0).default(0.01),
    chargeToleranceAmount: z.number().min(0).default(0.01),
  }),
  emits: [] as const,
});
